// The band. One component, two fills. Nothing is written on it; the ticks sit underneath.
import { type Day, fillFor, lightStops, weatherStops, gradient, pct, rainSpans, high, hhmm } from "../lib/band";

type Props = { day: Day; ticks?: boolean; title?: string; right?: string; compact?: boolean };

export function Band({ day, ticks = true, title, right, compact }: Props) {
  const fill = fillFor(day);
  const weather = fill === "weather" && day.hourly;
  const stops = weather ? weatherStops(day.hourly!) : lightStops(day.rise, day.set);
  const rides = day.rides ?? [];
  const kept = day.kept ?? [];
  const label = describe(day, fill);
  return (
    <div class={`band-wrap${compact ? " compact" : ""}`}>
      {(title || right) && (
        <div class="band-head"><span class="label ink">{title}</span><span class="label">{right}</span></div>
      )}
      <div class={`band${day.rest ? " rest" : ""}`} role="img" aria-label={label} style={{ background: gradient(stops) }}>
        {weather && rainSpans(day.hourly!).map((r) => (
          <span class="band-rain" style={{ left: pct(r.start), width: `calc(${pct(r.end)} - ${pct(r.start)})` }} />
        ))}
        {weather && <><span class="band-tick" style={{ left: pct(day.rise) }} /><span class="band-tick" style={{ left: pct(day.set) }} /></>}
        {kept.map((k) => <span class="band-kept" style={{ left: pct(k.start), width: `calc(${pct(k.end)} - ${pct(k.start)})` }} />)}
        {rides.map((r) => <span class={`band-ride${r.done ? " done" : ""}`} style={{ left: pct(r.start), width: `calc(${pct(r.end)} - ${pct(r.start)})` }} />)}
      </div>
      {ticks && (
        <div class="band-ticks">
          <span class="label">05:00</span>
          {weather ? (
            <>
              <span class="label amber">High {Math.round(high(day.hourly!).feels)}° at {hhmm(high(day.hourly!).at)}</span>
              <span class="label">{rainLabel(day)}</span>
            </>
          ) : (
            <>
              <span class="label amber">Sunrise {hhmm(day.rise)}</span>
              <span class="label dusk">Sunset {hhmm(day.set)}</span>
            </>
          )}
          <span class="label">22:00</span>
        </div>
      )}
    </div>
  );
}

function rainLabel(day: Day): string {
  const spans = rainSpans(day.hourly!);
  if (!spans.length) return "Dry";
  return `Rain ${spans.map((s) => `${hhmm(s.start).slice(0, 2)} to ${hhmm(s.end).slice(0, 2)}`).join(", ")}`;
}

function describe(day: Day, fill: string): string {
  const parts = [`${day.date}, 05:00 to 22:00`];
  parts.push(fill === "light" ? `sunrise ${hhmm(day.rise)}, sunset ${hhmm(day.set)}` : "filled with the hourly forecast");
  for (const r of day.rides ?? []) parts.push(`${r.done ? "ride done" : "ride"} ${hhmm(r.start)} to ${hhmm(r.end)}`);
  for (const k of day.kept ?? []) parts.push(`${k.label} ${hhmm(k.start)} to ${hhmm(k.end)}`);
  if (day.rest) parts.push("rest day");
  return parts.join(", ");
}
